import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import type { ChartData, ChartSpec, CopilotAnswer, EvidenceFact } from './copilotTypes';
import { Histogram } from './charts/Histogram';
import { CalendarHeatmap } from './charts/CalendarHeatmap';
import { KpiCards } from './charts/KpiCards';
import { TrendLine } from './charts/TrendLine';
import { ScatterRegression } from './charts/ScatterRegression';
import { PolarBar } from './charts/PolarBar';
import { Bubble } from './charts/Bubble';
import { StatCard } from './charts/StatCard';
import { Markdown } from './Markdown';
import { tokens } from './tokens';

function Chart({ spec, data }: { spec: ChartSpec; data: ChartData }) {
  const props = { title: '', spec, data } as any;
  switch (spec.chart) {
    case 'histogram': return <Histogram {...props} />;
    case 'calendar_heatmap': return <CalendarHeatmap {...props} />;
    case 'kpi': return <KpiCards {...props} />;
    case 'trend_line': return <TrendLine {...props} />;
    case 'scatter_regression': return <ScatterRegression {...props} />;
    case 'polar_bar': return <PolarBar {...props} />;
    case 'bubble': return <Bubble {...props} />;
    case 'stat': return <StatCard title="" data={data as any} />;
    default: return null;
  }
}

function Fact({ fact }: { fact: EvidenceFact }) {
  return (
    <Box sx={{ border: `1px solid ${tokens.surface.border}`, borderRadius: '10px', p: 1.25 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.text.primary, fontFamily: tokens.fontFamily }}>{fact.title}</Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
        {fact.values.map((value) => (
          <Chip
            key={`${fact.id}-${value.label}`}
            size="small"
            variant="outlined"
            label={`${value.label}: ${value.value}${value.unit ? ` ${value.unit}` : ''}${value.n !== undefined ? ` · n=${value.n}` : ''}`}
            sx={{ fontFamily: tokens.fontFamily, borderColor: tokens.surface.border, color: tokens.text.secondary }}
          />
        ))}
      </Stack>
      {fact.caveats.map((caveat) => <Typography key={caveat} sx={{ mt: 0.75, fontSize: 12, color: tokens.text.muted }}>{caveat}</Typography>)}
    </Box>
  );
}

export function AnalysisView({ answer }: { answer: Extract<CopilotAnswer, { kind: 'analysis' }> }) {
  const selected = new Set(answer.selectedFindingIds || answer.packet.findings.map((finding) => finding.id));
  const facts = answer.packet.facts.filter((fact) => answer.packet.findings.some((finding) => selected.has(finding.id) && finding.evidenceIds.includes(fact.id)));
  return (
    <Box>
      <Markdown text={answer.narrative} />
      <Stack gap={1} sx={{ mt: 1.25 }}>
        {answer.packet.findings.filter((finding) => selected.has(finding.id)).map((finding) => (
          <Box key={finding.id}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: tokens.text.primary, fontFamily: tokens.fontFamily }}>{finding.title}</Typography>
            <Typography sx={{ fontSize: 13, color: tokens.text.secondary, mt: 0.25 }}>{finding.statement}</Typography>
            {finding.caveats.map((caveat) => <Typography key={caveat} sx={{ fontSize: 12, color: tokens.text.muted, mt: 0.5 }}>{caveat}</Typography>)}
          </Box>
        ))}
      </Stack>
      {facts.length > 0 && <><Divider sx={{ my: 1.5 }} /><Stack gap={1}>{facts.map((fact) => <Fact key={fact.id} fact={fact} />)}</Stack></>}
      {answer.packet.caveats.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: tokens.text.muted, fontFamily: tokens.fontFamily }}>Caveats</Typography>
          {answer.packet.caveats.map((caveat) => <Typography key={caveat} sx={{ fontSize: 12, color: tokens.text.muted, mt: 0.5 }}>• {caveat}</Typography>)}
        </Box>
      )}
      {answer.supportingCharts.length > 0 && (
        <Stack gap={1.5} sx={{ mt: 1.5 }}>
          {answer.supportingCharts.map((chart) => <Box key={chart.title}><Chart spec={chart.spec} data={chart.data} /></Box>)}
        </Stack>
      )}
    </Box>
  );
}
